import React, { useState } from 'react'
import withApolloProvider from 'components/hocs/withApolloProvider'
import withReduxProvider from 'components/hocs/withReduxProvider'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import pickby from 'lodash.pickby'
import identity from 'lodash.identity'
import { Collapse } from 'react-collapse'
import Font from '@cred/react-font'
import { Testable } from '@cred/react-testable'
import { collapsibleClick } from '@cred/react-analytics/events'
import { withSnowplowContexts } from '@cred/react-analytics/SnowplowContextManager'
import { withStyles } from '@cred/react-theme'
import Dashboard from './dashboard/App'
import CompareRates from './compare-rates'
import FilterForm from './filter-form'
import useProductsAndFormHandler from './shared/hooks/useProductsAndFormHandler'
import SortingForm from './sorting-form'
import RateWidgetFooter from './footer'
import defaultStyles from '../themes/default.module.scss'
import fox from '../themes/fox.module.scss'
import homes1 from '../themes/homes1.module.scss'

import {
  PURCHASE_TYPE,
  REFINANCE_TYPE,
  MORTGAGE_FILTER_FORM_NAME,
} from './common/constants'
import {
  generateOnGetStartedCallback,
  getFilterText,
  mortgageWidgetMapStateToProps,
} from './common/utils'

const Widget = props => {
  const {
    variation,
    analyticsContexts,
    trackSnowplowEvent,
    formData,
    /* eslint-disable-next-line */
    utms: { utm_campaign, utm_source, utm_medium, utm_content },
    initialValues,
    partnerFormData,
    sortBy,
    termState,
    loanTypeState,
    isPurchase,
    styles,
    theme,
  } = props

  const [
      submitFormValues,
      formValues,
      currentProducts,
      loadingProducts,
      error,
    ] = useProductsAndFormHandler({ initialValues, partnerFormData }),
    [isOpen, setIsOpen] = useState(false),
    isFullTable = variation !== 'shortened',
    termFilters = Object.entries(termState)
      .filter(v => v[1])
      .map(v => v[0]),
    utmParams = pickby(
      {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
      },
      identity,
    ),
    onGetStarted = generateOnGetStartedCallback({ utmParams, isPurchase }),
    { sorting } = initialValues

  // ARM disabled tmp
  const products = (currentProducts[loanTypeState] || []).filter(
      ({ productType }) => productType !== 'fiveOneARM',
    ),
    filteredProducts = termFilters.length
      ? products.filter(({ productType }) => termFilters.some(e => e === productType))
      : products,
    sortedProducts = filteredProducts.sort((p1, p2) => p1[sortBy] - p2[sortBy]),
    bestProducts = sortedProducts.slice(0, 8),
    toggleCollapse = () => {
      setIsOpen(!isOpen)
      trackSnowplowEvent(collapsibleClick, {
        text: 'Edit icon',
        action: isOpen ? 'collapse' : 'expand',
      })
    },
    editButton = opened => (
      <Testable className={styles.captureHeader} onClick={toggleCollapse} testId="header-capture">
        <Font className={styles.captureTitle} type="body">
          {getFilterText(formValues, termState, isPurchase)}
        </Font>
        <i
          className={`${styles.editButton} ${
            opened ? styles.editButtonOpened : ''
          } cred-icon icon-edit`}
        />
      </Testable>
    )

  const filterFormProps = {
    analyticsContexts,
    loadingProducts,
    initialValues: formValues,
    form: MORTGAGE_FILTER_FORM_NAME,
    onSubmit: () => submitFormValues(formData),
  }

  return (
    <Testable className={styles.container} testId="rate-widget-container">
      {loadingProducts && <div className={styles.overlay} />}
      <Testable className={styles.header} testId="rate-widget-header">
        <Font className={styles.title} type="large" weight="bold">
          Today's Mortgage Rates
        </Font>
        <div className={styles.capture}>
          {editButton(isOpen)}
          <Collapse theme={{ collapse: 'collapseContainer' }} isOpened={isOpen}>
            <FilterForm {...filterFormProps} />
          </Collapse>
        </div>
        <SortingForm initialValues={{ sorting }} className={styles.inputWrapper} />
      </Testable>
      <Dashboard
        onGetStarted={onGetStarted}
        error={error}
        loading={loadingProducts}
        products={bestProducts}
        isFullTable={isFullTable}
        analyticsContexts={analyticsContexts}
      />
      {isFullTable && <CompareRates onGetStarted={onGetStarted} analyticsContexts={analyticsContexts} />}
      <RateWidgetFooter theme={theme} />
    </Testable>
  )
}

Widget.propTypes = {
  variation: PropTypes.string.isRequired,
  theme: PropTypes.string.isRequired,
  trackSnowplowEvent: PropTypes.func.isRequired,
  analyticsContexts: PropTypes.arrayOf(
    PropTypes.shape({
      schema: PropTypes.string,
      data: PropTypes.object,
    }),
  ),
  formData: PropTypes.object,
  utms: PropTypes.object.isRequired,
  partnerFormData: PropTypes.object,
  initialValues: PropTypes.object,
  styles: PropTypes.object.isRequired,
  sortBy: PropTypes.string,
  termState: PropTypes.object,
  loanTypeState: PropTypes.oneOf([PURCHASE_TYPE, REFINANCE_TYPE]),
  isPurchase: PropTypes.bool,
}

Widget.defaultProps = {
  analyticsContexts: [],
  formData: {},
  partnerFormData: undefined,
  initialValues: null,
}

const App = compose(
  withApolloProvider,
  withReduxProvider,
  withSnowplowContexts,
  withStyles(defaultStyles, { fox, homes1 }),
  connect(mortgageWidgetMapStateToProps),
)(Widget)

export { Widget }
export default App
